import { useTranslation } from 'react-i18next'
import { GridTable, GridTableHeader, GridTableRow } from '@/components/ui/grid-table'
import type { FaceMatchingIdentityRow } from '@/services/api/face-matching'
import { FaceTile } from './FaceTile'
import { formatScore } from './format'
import { SuggestedMatchesBadge } from './MatchScore'

const COLUMNS = '56px minmax(0,1fr) 120px minmax(0,220px)'
// The four-column table needs a desktop width: below lg the name column would
// collapse to nothing. There each row stacks beside its photo instead — name,
// pending count, strongest suggestion — and the header row is not shown. The
// row's inline column template is the table's, hence the important flag.
const STACKED_ROW = 'max-lg:grid-cols-[40px_minmax(0,1fr)]! max-lg:gap-x-3 max-lg:gap-y-0.5'

export interface FaceIdentityRowsProps {
  identities: FaceMatchingIdentityRow[]
  selectedPersonId: string | null
  onSelect: (personId: string) => void
}

/**
 * One row per identity with pending suggestions, in the order the API returns
 * them: strongest pending suggestion first. The last column carries that
 * similarity and how many suggestions earned the *suggested match* label
 * (ADR-0040). Pure — the caller owns the data.
 */
export function FaceIdentityRows({
  identities,
  selectedPersonId,
  onSelect,
}: FaceIdentityRowsProps) {
  const { t } = useTranslation()
  const base = 'cases.investigate.entityResolution.photoMatching'

  if (identities.length === 0) {
    return <p className="px-4 py-6 text-sm text-muted-foreground">{t(`${base}.empty`)}</p>
  }

  return (
    <GridTable columns={COLUMNS}>
      <GridTableHeader className="max-lg:hidden">
        <div />
        <div>{t(`${base}.columns.identity`)}</div>
        <div>{t(`${base}.columns.pending`)}</div>
        <div>{t(`${base}.columns.strongest`)}</div>
      </GridTableHeader>
      <div>
        {identities.map(identity => (
          <GridTableRow
            key={identity.person_id}
            active={identity.person_id === selectedPersonId}
            onClick={() => onSelect(identity.person_id)}
            data-person-id={identity.person_id}
            className={STACKED_ROW}
          >
            <FaceTile
              url={identity.photo_url}
              alt={identity.display_name ?? t(`${base}.unknownIdentity`)}
              size="sm"
              className="max-lg:row-span-3"
            />
            <div className="truncate font-medium">
              {identity.display_name ?? t(`${base}.unknownIdentity`)}
            </div>
            <div className="tabular-nums text-muted-foreground">
              {t(`${base}.pendingCount`, { count: identity.pending_total })}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium tabular-nums">{formatScore(identity.top_score)}</span>
              <SuggestedMatchesBadge count={identity.suggested_matches} />
            </div>
          </GridTableRow>
        ))}
      </div>
    </GridTable>
  )
}
